import classNames from "classnames";
import {
  Children,
  cloneElement,
  DialogHTMLAttributes,
  FC,
  isValidElement,
  PointerEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import Button from "components/Button";

import { ReactComponent as CloseSVG } from "assets/images/common/close.svg";

import useHideBodyScroll from "hooks/useHideBodyScroll";

import { useAppDispatch } from "store/hooks";
import { hideModal } from "store/slices/modalSlice";

type PropTypes = DialogHTMLAttributes<HTMLDialogElement> & {
  title?: string;
  isClose?: boolean;
  isCloseBg?: boolean;
  isVisible: boolean;
  onClose?: () => void;
};

const Modal: FC<PropTypes> = ({ isVisible, className, children, isClose, isCloseBg, title, onClose, ...props }) => {
  const ref = useRef<HTMLDialogElement>(null);
  const dispatch = useAppDispatch();
  const [isShow, setShow] = useState(false);

  useHideBodyScroll(isShow);

  const handleClose = useCallback(() => {
    ref?.current?.close();
    dispatch(hideModal());

    if (onClose) onClose();
  }, [dispatch, onClose]);

  const handleCloseModal = useCallback(() => {
    if (!isCloseBg && !isClose) return;

    handleClose();
  }, [isCloseBg, isClose, handleClose]);

  const handleDialogCancel = useCallback(
    (e: SyntheticEvent<HTMLDialogElement>) => {
      e.preventDefault();
      if (!isCloseBg && !isClose) return;
      handleClose();
    },
    [isCloseBg, isClose, handleClose],
  );

  const handleDialogPointerDown = useCallback(
    (e: PointerEvent<HTMLDialogElement>) => {
      if (!isCloseBg && !isClose) return;
      if (e.target !== e.currentTarget) return;
      handleClose();
    },
    [isCloseBg, isClose, handleClose],
  );

  const clonedChildren = Children.map(children, (child) => {
    if (isValidElement(child)) {
      return cloneElement(child, { onClose: handleClose } as Partial<unknown>);
    }

    return child;
  });

  useEffect(() => {
    if (!!ref.current) {
      if (isShow) {
        ref?.current?.showModal();
      } else {
        ref?.current?.close();
      }
    }
  }, [isShow]);

  useEffect(() => {
    setShow(isVisible);
  }, [isVisible]);

  return (
    <div className={classNames("modal", className)}>
      <dialog
        ref={ref}
        className="modal-dialog"
        {...props}
        onCancel={handleDialogCancel}
        onPointerDown={handleDialogPointerDown}
      >
        <div className="modal-panel">
          <div className="modal-headline">{!!title && <h1 className="modal-title">{title}</h1>}</div>

          {clonedChildren}
        </div>

        {isClose && (
          <Button type="button" className="modal-close" onClick={handleCloseModal} form="circle" variant="white">
            <CloseSVG />
          </Button>
        )}
      </dialog>
    </div>
  );
};

export default Modal;
